import { memo, useMemo } from "react";
import { rgbToCss } from "utils/rgbToCss";
import { getCircleSize } from "../../utils";
import { getPolygonLabelPoint } from "../getPolygonLabelPoint";
import { measureLabelWidth } from "../measureLabelWidth";
import { CREATION_RGB } from "../../RoutesCreationLayer/constants";
import {
  LABEL_CORNER_RADIUS_FACTOR,
  LABEL_ELLIPSIS,
  LABEL_FONT_FAMILY,
  LABEL_FONT_SIZE_FACTOR,
  LABEL_FONT_WEIGHT,
  LABEL_MAX_CHARACTERS,
  LABEL_OUTLINE_COLOR,
  LABEL_OUTLINE_WIDTH,
  LABEL_PADDING_X_FACTOR,
  LABEL_PADDING_Y_FACTOR,
  LABEL_TEXT_COLOR,
} from "../constants";
import type { PolygonLabelProps } from "./types";

const truncateLabel = (text: string): string =>
  text.length > LABEL_MAX_CHARACTERS
    ? text.slice(0, LABEL_MAX_CHARACTERS).trimEnd() + LABEL_ELLIPSIS
    : text;

export const PolygonLabel = memo(function PolygonLabel({
  polygon,
  zoomLevel,
  circleSizeScale,
}: PolygonLabelProps) {
  const label = truncateLabel(polygon.label ?? "");
  const color = rgbToCss(polygon.isDraft ? CREATION_RGB : polygon.rgb);
  const fontSize =
    getCircleSize(zoomLevel) * circleSizeScale * LABEL_FONT_SIZE_FACTOR;
  const paddingX = fontSize * LABEL_PADDING_X_FACTOR;
  const paddingY = fontSize * LABEL_PADDING_Y_FACTOR;

  const labelPoint = useMemo(
    () => getPolygonLabelPoint({ points: polygon.points }),
    [polygon.points],
  );

  const textWidth = useMemo(
    () =>
      measureLabelWidth({
        text: label,
        fontSize,
        fontFamily: LABEL_FONT_FAMILY,
        fontWeight: LABEL_FONT_WEIGHT,
      }),
    [label, fontSize],
  );

  if (label === "" || labelPoint === null) return null;

  const badgeWidth = textWidth + paddingX * 2;
  const badgeHeight = fontSize + paddingY * 2;

  return (
    <g style={{ pointerEvents: "none" }}>
      <rect
        x={labelPoint.x - badgeWidth / 2}
        y={labelPoint.y - badgeHeight / 2}
        width={badgeWidth}
        height={badgeHeight}
        rx={fontSize * LABEL_CORNER_RADIUS_FACTOR}
        fill={color}
        stroke={LABEL_OUTLINE_COLOR}
        strokeWidth={LABEL_OUTLINE_WIDTH}
        vectorEffect="non-scaling-stroke"
      />
      <text
        x={labelPoint.x}
        y={labelPoint.y}
        fill={LABEL_TEXT_COLOR}
        fontSize={fontSize}
        fontWeight={LABEL_FONT_WEIGHT}
        fontFamily={LABEL_FONT_FAMILY}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {label}
      </text>
    </g>
  );
});
